import {
  AlertSeverity,
  AnalysisType,
  Alert as AnalyzerAlert,
  CertificateSnapshot,
  CTSnapshot,
  DNSSnapshot,
  HTTPSnapshot,
  Job,
  SnapshotType
} from '@analyzer/client';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Alert as MuiAlert,
  Link as MuiLink,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useAnalysisCreateMutation, useAnalysisReadQuery } from '../api/analysis.js';
import { getSeverityColor } from '../components/alert/severity.js';
import { AlertComponent } from '../components/alert/view.js';
import { formatDateUTC } from '../components/utils.js';

const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const formatDateTime = (value: string | number | Date) =>
  new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value));

const SEVERITY_ORDER = ['high', 'medium', 'low', 'info'] as const;

function AlertsSummary({ alerts }: { alerts: AnalyzerAlert[] }) {
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const sev of SEVERITY_ORDER) map.set(sev, 0);
    for (const a of alerts)
      map.set((a as any).severity ?? 'info', (map.get((a as any).severity ?? 'info') ?? 0) + 1);
    return SEVERITY_ORDER.map((sev) => ({ severity: sev, count: map.get(sev) ?? 0 }));
  }, [alerts]);

  const total = alerts.length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="h5">Alerts Summary</Typography>
        <Typography variant="body2" color="text.secondary">
          Total: <b>{total}</b>
        </Typography>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.25} mt={1.25}>
        {counts.map(({ severity, count }) => (
          <Chip
            key={severity}
            label={`${count} ${severity}`}
            color={getSeverityColor(severity)}
            variant={count ? 'filled' : 'outlined'}
            size="small"
          />
        ))}
      </Stack>
    </Box>
  );
}

function AlertsDetails({ alerts }: { alerts: AnalyzerAlert[] }) {
  alerts.sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));

  if (!alerts.length) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Alerts Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No alerts detected for this analysis.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Alerts Details
      </Typography>
      <Stack spacing={2}>
        {alerts.map((alert, idx) => (
          <Box key={(alert as any).id ?? `${(alert as any).type ?? 'alert'}-${idx}`}>
            <AlertComponent alert={alert} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function HistoricalSnapshots({
  context,
  projectId,
  analysisType
}: {
  context: string[];
  projectId: string;
  analysisType: AnalysisType;
}) {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Historical Data
      </Typography>
      {!context.length ? (
        <Typography variant="body2" color="text.secondary">
          No historical snapshots used as context.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            The following snapshots were used as context in the analysis:
          </Typography>
          <Stack component="nav" spacing={0.75}>
            {context.map((snapshot, index) => (
              <MuiLink
                key={index}
                component={RouterLink}
                to={`/projects/${projectId}/snapshots/${analysisType}/${snapshot}`}
                underline="hover"
              >
                Snapshot {snapshot}
              </MuiLink>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

export interface AnalysisDialogProps {
  projectId: string;
  snapshotId: string;
  snapshotType: SnapshotType;
  open: boolean;
  onClose: () => void;
  onConfirm?: (job: Job) => void;
  onError?: (error: unknown) => void;
}

export function CreateSnapshotAnalysisDialog({
  open,
  onClose,
  onConfirm,
  onError,
  projectId,
  snapshotId,
  snapshotType
}: AnalysisDialogProps) {
  const [context, setContext] = useState(1);
  const [severityThreshold, setSeverityThreshold] = useState<AlertSeverity>('info');
  const [announce, setAnnounce] = useState(false);
  const createAnalysisMutation = useAnalysisCreateMutation();

  const handleContextChange = (e: any) => {
    setContext(Math.max(1, parseInt(e.target.value) || 1));
  };

  const handleSeverityChange = (event: SelectChangeEvent<AlertSeverity>) => {
    setSeverityThreshold(event.target.value as AlertSeverity);
  };

  const handleReplayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnnounce(event.target.checked);
  };

  const handleSubmit = () => {
    createAnalysisMutation.mutate(
      {
        projectId,
        snapshotId,
        snapshotType,
        context,
        severityThreshold,
        announce
      },
      {
        onSuccess: (job) => {
          onConfirm?.(job);
          onClose();
        },
        onError: (err) => {
          onError?.(err);
        }
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create New Snapshot Analysis</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} pt={1}>
          {createAnalysisMutation.isError && (
            <Alert severity="error">Failed to create analysis: {createAnalysisMutation.error.message}</Alert>
          )}

          {/* Context Selection */}
          <FormControl fullWidth>
            <TextField
              label="Context"
              type="number"
              value={context}
              inputProps={{ min: 1, step: 1 }}
              onChange={handleContextChange}
              helperText="Select the analysis context (1 = minimal, higher numbers expand the scope)."
            />
          </FormControl>

          {/* Severity Threshold */}
          <FormControl fullWidth>
            <InputLabel id="severity-threshold-label">Severity Threshold</InputLabel>
            <Select
              labelId="severity-threshold-label"
              value={severityThreshold}
              label="Severity Threshold"
              onChange={handleSeverityChange}
            >
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="info">Info</MenuItem>
            </Select>
            <FormHelperText>
              Only alerts at or above the selected threshold are included. For example, selecting <b>Low</b>{' '}
              includes Low, Medium, and High alerts.
            </FormHelperText>
          </FormControl>

          {/* Publish / Share */}
          <FormControl component="fieldset">
            <FormControlLabel
              control={<Switch checked={announce} onChange={handleReplayChange} />}
              label="Share reanalysis results"
            />
            <FormHelperText>
              Reanalyses are private by default. Enable this to publish results to the queue.
            </FormHelperText>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={createAnalysisMutation.isLoading}
          loadingIndicator={<CircularProgress size={20} />}
        >
          Create
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export function AnalysisView() {
  const { projectId, analysisId, analysisType } = useParams<{
    projectId: string;
    analysisId: string;
    analysisType: AnalysisType;
  }>();

  if (!projectId || !analysisId || !analysisType) throw new Error('Missing parameter');

  const analysis = useAnalysisReadQuery(projectId, analysisId, analysisType);

  const [openDialog, setOpenDialog] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  if (analysis.isLoading) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Skeleton variant="text" width={260} height={40} />
          <Skeleton variant="rounded" width={140} height={36} />
        </Stack>
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={80} />
        <Skeleton variant="rounded" height={320} />
      </Stack>
    );
  }

  if (analysis.isError) {
    return (
      <MuiAlert severity="error" variant="filled" sx={{ alignItems: 'flex-start' }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Error loading analysis
        </Typography>
        <Typography variant="body2">{(analysis.error as any)?.message ?? 'Unknown error'}</Typography>
      </MuiAlert>
    );
  }

  const { data } = analysis;

  let resource;
  switch (analysisType) {
    case 'web':
      resource = (data.snapshot as HTTPSnapshot).test.target;
      break;
    case 'transparency':
      resource = (data.snapshot as CTSnapshot).root.hostname;
      break;
    case 'dns':
      resource = (data.snapshot as DNSSnapshot).domain.name;
      break;
    case 'certificate':
      resource = (data.snapshot as CertificateSnapshot).resource.hostname;
      break;
  }

  return (
    <Stack spacing={2}>
      {/* Header */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4">{capitalize(analysisType)} Analysis</Typography>
            <Breadcrumbs aria-label="breadcrumbs" separator="/" sx={{ mt: 0.5 }}>
              <MuiLink component={RouterLink} to="/projects" underline="hover">
                Projects
              </MuiLink>
              <MuiLink component={RouterLink} to={`/projects/${data.project.id}`} underline="hover">
                {data.project.id}
              </MuiLink>

              <MuiLink component={RouterLink} to={`/projects/${data.project.id}/tests`} underline="hover">
                Analyses
              </MuiLink>

              <Typography color="text.secondary">{data.id}</Typography>
            </Breadcrumbs>
          </Box>

          {/* Buttons aligned to the right */}
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="primary" onClick={() => setOpenDialog(true)}>
              Analyze
            </Button>
            <Button
              component={RouterLink}
              to={`/projects/${projectId}/analyses/${analysisType}/${analysisId}/debug`}
              variant="outlined"
            >
              Developer View
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Metadata */}
      <Box>
        <Typography variant="h5" gutterBottom>
          Metadata
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          divider={<Divider flexItem orientation="vertical" />}
        >
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body1">{formatDateTime(data.createdDate)}</Typography>
          </Stack>
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Created (UTC)
            </Typography>
            <Typography variant="body1">{formatDateUTC(data.createdDate)}</Typography>
          </Stack>
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Project
            </Typography>
            <MuiLink component={RouterLink} to={`/projects/${data.project.id}`} underline="hover">
              {data.project.name}
            </MuiLink>
          </Stack>
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Resource
            </Typography>
            <Typography variant="body1">{resource}</Typography>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Analyzed Snapshot
            </Typography>
            <MuiLink
              component={RouterLink}
              to={`/projects/${projectId}/snapshots/${analysisType}/${data.snapshot.id}`}
              underline="hover"
            >
              {String(data.snapshot.id)}
            </MuiLink>
          </Stack>
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Total Alerts
            </Typography>
            <Typography variant="body1">
              <b>{data.alerts.length}</b>
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Historical Context */}
      <HistoricalSnapshots context={data.context} projectId={projectId} analysisType={analysisType} />

      {/* Alert Summary */}
      <AlertsSummary alerts={data.alerts} />

      {/* Alert Details */}
      <AlertsDetails alerts={data.alerts} />

      <CreateSnapshotAnalysisDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
        onConfirm={(job) => {
          enqueueSnackbar(`Successfully started analysis job #${job.id}`, { variant: 'success' });
        }}
        onError={(error) => {
          const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
          enqueueSnackbar(`Failed to start analysis: ${errorMessage}`, { variant: 'error' });
        }}
        projectId={data.project.id}
        snapshotId={data.snapshot.id}
        snapshotType={analysisType}
      />
    </Stack>
  );
}
